import {FormLayout, FormActions, TextField, SelectField} from '@hconnect/common/components'
import {yupResolver} from '@hookform/resolvers/yup'
import {isUndefined} from 'lodash'
import {useMemo} from 'react'
import {FormProvider, useForm, useFormState, SubmitHandler} from 'react-hook-form'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../../consts'
import {useTaskUpmPlantsOptions} from '../../hooks'
import {
  TaskSource,
  TaskStatus,
  MACHINE_SENTRY_ACTION_STATUSES,
  MachineSentryActionFormValues
} from '../../types'
import {getMachineSentryActionValidationSchema} from '../../validators'

import {EquipmentSearchField} from './EquipmentSearchField'
import {MachineSentryPriorityField} from './MachineSentryPriorityField'
import {MachineSentryUserSearchFormField} from './MachineSentryUserSearchFormField'
import {MainEquipmentSearchField} from './MainEquipmentSearchField'
import {ProcessStageField} from './ProcessStageField'
import {RichTextFormField, MomentDatePickerFormField} from './shared'
import {TaskStatusField} from './TaskStatusField'
import {TaskTypeField} from './TaskTypeField'

type Props = {
  onSubmit: SubmitHandler<MachineSentryActionFormValues>
  onCancel: () => void
  onTaskTypeChange: (taskType: TaskSource | undefined) => void
  plantId: string
  initialValues?: MachineSentryActionFormValues
  externalTaskId?: string
  timezone?: string
  onStatusChange?: (newValue: TaskStatus | null, resetStatus: () => void) => void
}

const defaultValues: Partial<MachineSentryActionFormValues> = {
  title: '',
  description: '',
  plantUpmId: undefined,
  status: TaskStatus.Pending,
  assignees: []
}

export const MachineSentryActionForm = ({initialValues, plantId, timezone, ...props}: Props) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const validationSchema = useMemo(() => getMachineSentryActionValidationSchema({t}), [t])

  const methods = useForm<MachineSentryActionFormValues>({
    mode: 'onChange',
    resolver: yupResolver(validationSchema),
    values: initialValues,
    ...(!initialValues && {defaultValues})
  })

  const mainEquipment = methods.watch('mainEquipment')

  const {isValid, isSubmitting} = useFormState({
    control: methods.control
  })

  const plantOptions = useTaskUpmPlantsOptions(TaskSource.MACHINE_SENTRY_ACTIONS)
  const plantUpmId = methods.watch('plantUpmId')

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(props.onSubmit)}>
        <FormLayout>
          <TaskTypeField
            value={TaskSource.MACHINE_SENTRY_ACTIONS}
            onChange={props.onTaskTypeChange}
          />
          <TextField name="title" label={t('task.label.title')} control={methods.control} />
          <RichTextFormField
            name="description"
            label={t('task.label.description')}
            pressAndHoldRecordLabel={t('task.label.pressAndHoldRecord')}
            control={methods.control}
          />
          {timezone && (
            <MomentDatePickerFormField
              name="dueDate"
              disableDateSwitch={true}
              label={t('task.label.dueDate')}
              control={methods.control}
              onChange={(date) => {
                if (date) {
                  methods.setValue('dueDate', date.toISOString(), {shouldValidate: true})
                }
              }}
              timezone={timezone}
            />
          )}
          <SelectField
            disabled={true} // Can't be changed when editing
            name="plantUpmId"
            label={t('task.label.plant')}
            control={methods.control}
            options={plantOptions}
          />
          <TaskStatusField
            name="status"
            label={t('task.label.status')}
            control={methods.control}
            allowedStatuses={MACHINE_SENTRY_ACTION_STATUSES}
            onChange={(newValue, previousValue) => {
              props.onStatusChange?.(newValue ?? null, () => {
                methods.setValue('status', previousValue as MachineSentryActionFormValues['status'])
              })
            }}
          />
          <MachineSentryPriorityField
            name="priority"
            label={t('task.label.priority')}
            control={methods.control}
          />
          <EquipmentSearchField
            plantId={plantId}
            name="equipment"
            label={t('task.label.equipment')}
            control={methods.control}
            onChange={(equipment) => {
              const mainEquipmentData = isUndefined(equipment)
                ? undefined
                : (methods.getValues('mainEquipment') ?? equipment?.mainEquipment)
              methods.setValue('mainEquipment', mainEquipmentData)
              methods.setValue('processStage', equipment?.processStage?.processStep)
            }}
            mainEquipmentId={mainEquipment?.id}
            optional
          />
          <MainEquipmentSearchField
            plantId={plantId}
            name="mainEquipment"
            label={t('task.label.mainEquipment')}
            control={methods.control}
            optional
          />
          <ProcessStageField
            plantId={plantId}
            name="processStage"
            label={t('task.label.processStage')}
            control={methods.control}
            optional
          />
          <MachineSentryUserSearchFormField
            plantUpmId={plantUpmId}
            name="assignees"
            label={t('task.label.assignee')}
            pleaseStartTypingLabel={t('task.label.pleaseStartTyping')}
            control={methods.control}
            optional
          />
          <FormActions onCancel={props.onCancel} isSubmitting={isSubmitting} isValid={isValid} />
        </FormLayout>
      </form>
    </FormProvider>
  )
}
